/**
 * Country & Currency Setup
 * Phase 1: Foundation & System Settings
 *
 * System Administrator page for managing multi-jurisdiction configuration
 * Supports both Demo mode (localStorage) and Live mode (API).
 */

import {
  AlertCircle,
  Calendar,
  Check,
  CheckCircle2,
  ChevronsUpDown,
  DollarSign,
  Globe,
  Info,
  Pause,
  Plus,
  RefreshCw,
  Settings,
  Star,
  Trash2,
  X,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useApp } from "@/contexts/AppContext";
import { useJurisdiction } from "@/contexts/JurisdictionContext";
import { useFormatting } from "@/hooks/useFormatting";
import { cn } from "@/lib/utils";
import {
  type CashFlowSettings,
  type Country,
  getCountryByCode,
  getTemplateForCountry,
  type Jurisdiction,
  STATUTORY_PAYMENT_TEMPLATES,
  type StatutoryPayment,
  TOP_50_COUNTRIES,
} from "@/types/jurisdiction";

// Local storage keys
const STORAGE_KEYS = {
  JURISDICTIONS: "cashflow_jurisdictions",
  SETTINGS: "cashflow_settings",
};

// Helper to generate unique IDs
const generateId = () =>
  `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Helper to calculate next due date
const getNextDueDate = (
  frequency: "Monthly" | "Quarterly" | "Annual",
  dayOfMonth: number,
): Date => {
  const today = new Date();
  const nextDate = new Date(today.getFullYear(), today.getMonth(), dayOfMonth);

  if (frequency === "Monthly") {
    if (nextDate <= today) {
      nextDate.setMonth(nextDate.getMonth() + 1);
    }
  } else if (frequency === "Quarterly") {
    const currentMonth = today.getMonth();
    const quarterMonths = [2, 5, 8, 11]; // March, June, September, December
    let nextQuarterMonth = quarterMonths.find((m) => m > currentMonth);

    if (nextQuarterMonth === undefined) {
      nextDate.setFullYear(nextDate.getFullYear() + 1);
      nextDate.setMonth(2);
    } else {
      nextDate.setMonth(nextQuarterMonth);
    }
    nextDate.setDate(dayOfMonth);
  } else if (frequency === "Annual") {
    nextDate.setMonth(2); // March
    nextDate.setDate(31);
    if (nextDate <= today) {
      nextDate.setFullYear(nextDate.getFullYear() + 1);
    }
  }

  return nextDate;
};

export function CountryCurrencySetup() {
  const { mode } = useApp();
  const {
    jurisdictions,
    setJurisdictions,
    defaultJurisdictionId,
    setDefaultJurisdictionId,
  } = useJurisdiction();
  const { formatDate } = useFormatting();

  const [settings, setSettings] = useState<CashFlowSettings>(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    return stored
      ? JSON.parse(stored)
      : {
          baseCurrency: "GBP",
          multiJurisdictionMode: false,
          lastUpdated: new Date(),
        };
  });

  // Note: Jurisdiction and CashFlowSettings API endpoints are not yet implemented in the backend.
  // This component uses demo data only (localStorage).

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
  }, [settings]);

  // Dialog states
  const [addJurisdictionOpen, setAddJurisdictionOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedJurisdiction, setSelectedJurisdiction] =
    useState<Jurisdiction | null>(null);

  // Add jurisdiction form state
  const [countrySearchOpen, setCountrySearchOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [primaryCurrency, setPrimaryCurrency] = useState("");
  const [reportingCurrencies, setReportingCurrencies] = useState<string[]>([]);
  const [consolidationCurrency, setConsolidationCurrency] = useState("");
  const [loadTemplate, setLoadTemplate] = useState<
    "scratch" | "template" | "copy"
  >("scratch");
  const [copyFromJurisdiction, setCopyFromJurisdiction] = useState("");
  const [newCurrencyInput, setNewCurrencyInput] = useState("");

  // Get list of already added country codes
  const addedCountryCodes = jurisdictions.map((j) => j.countryCode);

  // Available countries (not yet added)
  const availableCountries = TOP_50_COUNTRIES.filter(
    (c) => !addedCountryCodes.includes(c.code),
  );

  // Handle multi-jurisdiction mode toggle
  // Note: CashFlowSettings API endpoints are not yet implemented in the backend.
  const handleModeToggle = (enabled: boolean) => {
    const newSettings = {
      ...settings,
      multiJurisdictionMode: enabled,
      lastUpdated: new Date(),
    };
    setSettings(newSettings);

    toast.success(
      enabled
        ? "Multi-Jurisdiction Mode Enabled"
        : "Single Country Mode Enabled",
      {
        description: enabled
          ? "You can now manage multiple countries and currencies."
          : "System is now in single country mode.",
      },
    );
  };

  // Handle base currency change
  // Note: CashFlowSettings API endpoints are not yet implemented in the backend.
  const handleBaseCurrencyChange = (currency: string) => {
    const newSettings = {
      ...settings,
      baseCurrency: currency,
      lastUpdated: new Date(),
    };
    setSettings(newSettings);

    toast.success("Base Currency Updated", {
      description: `Group reporting currency set to ${currency}`,
    });
  };

  // Handle country selection
  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setPrimaryCurrency(country.currency);
    setConsolidationCurrency(country.currency);
    setReportingCurrencies([]);
    setCountrySearchOpen(false);
  };

  // Add reporting currency
  const handleAddReportingCurrency = () => {
    if (newCurrencyInput && !reportingCurrencies.includes(newCurrencyInput)) {
      setReportingCurrencies([...reportingCurrencies, newCurrencyInput]);
      setNewCurrencyInput("");
    }
  };

  // Remove reporting currency
  const handleRemoveReportingCurrency = (currency: string) => {
    setReportingCurrencies(reportingCurrencies.filter((c) => c !== currency));
  };

  // Reset add jurisdiction form
  const resetAddForm = () => {
    setSelectedCountry(null);
    setPrimaryCurrency("");
    setReportingCurrencies([]);
    setConsolidationCurrency("");
    setLoadTemplate("scratch");
    setCopyFromJurisdiction("");
    setNewCurrencyInput("");
  };

  // Handle add jurisdiction
  // Note: Jurisdiction API endpoints are not yet implemented in the backend.
  const handleAddJurisdiction = () => {
    if (!selectedCountry || !primaryCurrency || !consolidationCurrency) {
      toast.error("Missing Required Fields", {
        description: "Please fill in all required fields.",
      });
      return;
    }

    // Check if the country has already been set up as a jurisdiction
    const existingJurisdiction = jurisdictions.find(
      (j) => j.countryCode === selectedCountry.code,
    );
    if (existingJurisdiction) {
      toast.error("Country Already Exists", {
        description: `${selectedCountry.name} has already been set up as a jurisdiction. Please select a different country.`,
      });
      return;
    }

    // Create statutory payments based on template selection
    let statutoryPayments: StatutoryPayment[] = [];

    if (loadTemplate === "template") {
      const template = getTemplateForCountry(selectedCountry.code);
      if (template) {
        statutoryPayments = template.payments.map((p) => ({
          id: generateId(),
          jurisdictionId: "", // Will be set after jurisdiction is created
          name: p.name,
          frequency: p.frequency,
          dayOfMonth: p.dayOfMonth,
          nextDue: getNextDueDate(p.frequency, p.dayOfMonth),
          isActive: true,
        }));
      }
    } else if (loadTemplate === "copy" && copyFromJurisdiction) {
      const sourceJurisdiction = jurisdictions.find(
        (j) => j.id === copyFromJurisdiction,
      );
      if (sourceJurisdiction) {
        statutoryPayments = sourceJurisdiction.statutoryPayments.map((p) => ({
          ...p,
          id: generateId(),
          jurisdictionId: "", // Will be set after jurisdiction is created
        }));
      }
    }

    // Use localStorage for demo mode
    const newJurisdiction: Jurisdiction = {
      id: generateId(),
      countryCode: selectedCountry.code,
      countryName: selectedCountry.name,
      primaryCurrency,
      reportingCurrencies,
      consolidationCurrency,
      isActive: true,
      statutoryPayments: statutoryPayments.map((p) => ({
        ...p,
        jurisdictionId: "",
      })),
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Update jurisdiction IDs in statutory payments
    newJurisdiction.statutoryPayments = newJurisdiction.statutoryPayments.map(
      (p) => ({
        ...p,
        jurisdictionId: newJurisdiction.id,
      }),
    );

    setJurisdictions([...jurisdictions, newJurisdiction]);

    toast.success("Jurisdiction Added", {
      description: `${selectedCountry.name} has been added successfully.`,
    });

    setAddJurisdictionOpen(false);
    resetAddForm();
  };

  // Handle toggle jurisdiction active status
  // Note: Jurisdiction API endpoints are not yet implemented in the backend.
  const handleToggleActive = (jurisdiction: Jurisdiction) => {
    const newIsActive = !jurisdiction.isActive;

    setJurisdictions(
      jurisdictions.map((j) =>
        j.id === jurisdiction.id
          ? { ...j, isActive: newIsActive, updatedAt: new Date() }
          : j,
      ),
    );

    toast.success(
      jurisdiction.isActive
        ? "Jurisdiction Disabled"
        : "Jurisdiction Activated",
      {
        description: `${jurisdiction.countryName} has been ${
          jurisdiction.isActive ? "disabled" : "activated"
        }.`,
      },
    );
  };

  // Handle delete jurisdiction
  // Note: Jurisdiction API endpoints are not yet implemented in the backend.
  const handleDeleteJurisdiction = () => {
    if (!selectedJurisdiction) return;

    setJurisdictions(
      jurisdictions.filter((j) => j.id !== selectedJurisdiction.id),
    );

    toast.success("Jurisdiction Removed", {
      description: `${selectedJurisdiction.countryName} has been removed.`,
    });

    setDeleteDialogOpen(false);
    setSelectedJurisdiction(null);
  };

  // Handle set default jurisdiction
  // Note: Jurisdiction API endpoints are not yet implemented in the backend.
  const handleSetDefault = (jurisdictionId: string) => {
    setDefaultJurisdictionId(jurisdictionId);

    const jurisdiction = jurisdictions.find((j) => j.id === jurisdictionId);
    toast.success("Default Jurisdiction Set", {
      description: `${jurisdiction?.countryName} will be automatically selected when you open this page.`,
    });
  };

  // Get unique currencies from all jurisdictions
  const allCurrencies = Array.from(
    new Set([
      ...jurisdictions.map((j) => j.primaryCurrency),
      ...jurisdictions.flatMap((j) => j.reportingCurrencies),
    ]),
  ).sort();

  return (
    <div className="space-y-6 p-6">
      <div className="flex justify-end">
        <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-700">
          Demo Mode - Changes saved to browser only
        </span>
      </div>

      {/* Multi-Jurisdiction Mode Toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Multi-Jurisdiction Mode
          </CardTitle>
          <CardDescription>
            Enable this to manage cash flow across multiple countries
            simultaneously
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="multi-jurisdiction-mode" className="text-base">
                {settings.multiJurisdictionMode
                  ? "Multiple Countries"
                  : "Single Country"}
              </Label>
              <p className="text-sm text-muted-foreground">
                {settings.multiJurisdictionMode
                  ? "Managing cash flow for multiple jurisdictions"
                  : "Managing cash flow for a single jurisdiction"}
              </p>
            </div>
            <Switch
              id="multi-jurisdiction-mode"
              checked={settings.multiJurisdictionMode}
              onCheckedChange={handleModeToggle}
            />
          </div>

          {settings.multiJurisdictionMode && (
            <div className="pt-4 border-t space-y-4">
              <div className="space-y-2">
                <Label>Base Currency (Group Reporting)</Label>
                <Select
                  value={settings.baseCurrency}
                  onValueChange={handleBaseCurrencyChange}
                >
                  <SelectTrigger className="w-[300px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {allCurrencies.length > 0 ? (
                      allCurrencies.map((currency) => (
                        <SelectItem key={currency} value={currency}>
                          {currency}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="GBP">GBP</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Currency used for consolidated group-level reporting
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Jurisdictions List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Jurisdictions
              </CardTitle>
              <CardDescription>
                {jurisdictions.length === 0
                  ? "No jurisdictions configured yet"
                  : `${jurisdictions.length} ${
                      jurisdictions.length === 1
                        ? "jurisdiction"
                        : "jurisdictions"
                    } configured`}
              </CardDescription>
            </div>
            <Button
              onClick={() => setAddJurisdictionOpen(true)}
              disabled={!settings.multiJurisdictionMode}
              title={
                !settings.multiJurisdictionMode
                  ? "Enable Multi-Jurisdiction Mode to add countries"
                  : undefined
              }
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Country
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {jurisdictions.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed rounded-lg">
              <Globe className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                No Jurisdictions Yet
              </h3>
              <p className="text-muted-foreground mb-4">
                Add your first country to start managing multi-jurisdiction cash
                flow
              </p>
              <Button
                onClick={() => setAddJurisdictionOpen(true)}
                disabled={!settings.multiJurisdictionMode}
                title={
                  !settings.multiJurisdictionMode
                    ? "Enable Multi-Jurisdiction Mode to add countries"
                    : undefined
                }
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Country
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {jurisdictions.map((jurisdiction) => {
                const country = getCountryByCode(jurisdiction.countryCode);
                const hasTemplate = !!getTemplateForCountry(
                  jurisdiction.countryCode,
                );

                return (
                  <Card key={jurisdiction.id} className="border-2">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          <div className="text-4xl">
                            {country?.flag || "🌍"}
                          </div>
                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold flex items-center gap-1">
                                {jurisdiction.countryName}
                                {jurisdiction.id === defaultJurisdictionId && (
                                  <span
                                    className="text-amber-500 text-sm"
                                    title="Default jurisdiction"
                                  >
                                    ⭐
                                  </span>
                                )}
                              </h3>
                              <Badge
                                variant={
                                  jurisdiction.isActive
                                    ? "default"
                                    : "secondary"
                                }
                              >
                                {jurisdiction.isActive ? (
                                  <>
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Active
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="h-3 w-3 mr-1" />
                                    Inactive
                                  </>
                                )}
                              </Badge>
                              {hasTemplate && (
                                <Badge
                                  variant="outline"
                                  className="bg-blue-50 text-blue-700 border-blue-200"
                                >
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Template Available
                                </Badge>
                              )}
                            </div>

                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-muted-foreground">
                                  Primary Currency
                                </p>
                                <p className="font-medium">
                                  {jurisdiction.primaryCurrency}
                                </p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">
                                  Consolidation Currency
                                </p>
                                <p className="font-medium">
                                  {jurisdiction.consolidationCurrency}
                                </p>
                              </div>
                            </div>

                            {jurisdiction.reportingCurrencies.length > 0 && (
                              <div className="text-sm">
                                <p className="text-muted-foreground">
                                  Additional Reporting Currencies
                                </p>
                                <div className="flex gap-1 mt-1">
                                  {jurisdiction.reportingCurrencies.map(
                                    (currency) => (
                                      <Badge key={currency} variant="outline">
                                        {currency}
                                      </Badge>
                                    ),
                                  )}
                                </div>
                              </div>
                            )}

                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {jurisdiction.statutoryPayments.length}{" "}
                                Statutory Payments
                              </div>
                              <div className="flex items-center gap-1">
                                <DollarSign className="h-4 w-4" />
                                Last updated:{" "}
                                {formatDate(new Date(jurisdiction.updatedAt))}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSetDefault(jurisdiction.id)}
                            title="Set as default jurisdiction"
                          >
                            <Star
                              className={cn(
                                "h-4 w-4",
                                jurisdiction.id === defaultJurisdictionId &&
                                  "fill-amber-500 text-amber-500",
                              )}
                            />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleActive(jurisdiction)}
                          >
                            <Pause className="h-4 w-4 mr-1" />
                            {jurisdiction.isActive ? "Disable" : "Activate"}
                          </Button>
                          <Button variant="outline" size="sm">
                            <Settings className="h-4 w-4 mr-1" />
                            Configure
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedJurisdiction(jurisdiction);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Remove
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Exchange Rates Info Card */}
      <Card className="border-blue-500/50 bg-blue-500/5">
        <CardContent className="flex items-start gap-3 pt-6">
          <Info className="h-5 w-5 text-blue-500 mt-0.5" />
          <div className="space-y-2 flex-1">
            <p className="font-medium">Exchange Rates</p>
            <p className="text-sm text-muted-foreground">
              Exchange rates are automatically synchronized from your accounting
              system (Xero/QuickBooks). Manual sync will be available once
              accounting integration is configured.
            </p>
            <Button variant="outline" size="sm" disabled className="mt-2">
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync Exchange Rates
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Add Jurisdiction Dialog */}
      <Dialog open={addJurisdictionOpen} onOpenChange={setAddJurisdictionOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Jurisdiction</DialogTitle>
            <DialogDescription>
              Add a new country to manage cash flow across multiple
              jurisdictions
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Country Selection */}
            <div className="space-y-2">
              <Label>
                Country <span className="text-destructive">*</span>
              </Label>
              <Popover
                open={countrySearchOpen}
                onOpenChange={setCountrySearchOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={countrySearchOpen}
                    className="w-full justify-between"
                  >
                    {selectedCountry ? (
                      <span className="flex items-center gap-2">
                        <span className="text-xl">{selectedCountry.flag}</span>
                        {selectedCountry.name}
                      </span>
                    ) : (
                      "Search countries..."
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[600px] p-0">
                  <Command>
                    <CommandInput placeholder="Search countries..." />
                    <CommandEmpty>No country found.</CommandEmpty>
                    <CommandGroup className="max-h-[300px] overflow-y-auto">
                      {availableCountries.map((country) => (
                        <CommandItem
                          key={country.code}
                          value={country.name}
                          onSelect={() => handleCountrySelect(country)}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedCountry?.code === country.code
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                          <span className="text-xl mr-2">{country.flag}</span>
                          <div className="flex-1">
                            <div className="font-medium">{country.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {country.region} • {country.currency}
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
              {availableCountries.length === 0 && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  All countries have been added. Remove a jurisdiction to add
                  another.
                </p>
              )}
            </div>

            {/* Primary Currency */}
            {selectedCountry && (
              <>
                <div className="space-y-2">
                  <Label>
                    Primary Currency <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    value={primaryCurrency}
                    onChange={(e) => setPrimaryCurrency(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Auto-detected: {selectedCountry.currencyName}
                  </p>
                </div>

                {/* Additional Reporting Currencies */}
                <div className="space-y-2">
                  <Label>Additional Reporting Currencies (Optional)</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g., USD, EUR"
                      value={newCurrencyInput}
                      onChange={(e) =>
                        setNewCurrencyInput(e.target.value.toUpperCase())
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddReportingCurrency();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddReportingCurrency}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {reportingCurrencies.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {reportingCurrencies.map((currency) => (
                        <Badge
                          key={currency}
                          variant="secondary"
                          className="gap-1"
                        >
                          {currency}
                          <button
                            type="button"
                            onClick={() =>
                              handleRemoveReportingCurrency(currency)
                            }
                            className="ml-1 hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Some countries report in multiple currencies (e.g.,
                    Bangladesh reports in BDT and USD)
                  </p>
                </div>

                {/* Consolidation Currency */}
                <div className="space-y-2">
                  <Label>
                    Consolidation Currency{" "}
                    <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={consolidationCurrency}
                    onValueChange={setConsolidationCurrency}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={primaryCurrency}>
                        {primaryCurrency}
                      </SelectItem>
                      {reportingCurrencies.map((currency) => (
                        <SelectItem key={currency} value={currency}>
                          {currency}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    Used when rolling up to group consolidated view
                  </p>
                </div>

                {/* Load Template */}
                <div className="space-y-3 pt-4 border-t">
                  <Label>Load Template (Optional)</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="scratch"
                        name="template"
                        value="scratch"
                        checked={loadTemplate === "scratch"}
                        onChange={(e) => setLoadTemplate(e.target.value as any)}
                        className="h-4 w-4"
                      />
                      <Label
                        htmlFor="scratch"
                        className="font-normal cursor-pointer"
                      >
                        Start from scratch
                      </Label>
                    </div>

                    {getTemplateForCountry(selectedCountry.code) && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="template"
                          name="template"
                          value="template"
                          checked={loadTemplate === "template"}
                          onChange={(e) =>
                            setLoadTemplate(e.target.value as any)
                          }
                          className="h-4 w-4"
                        />
                        <Label
                          htmlFor="template"
                          className="font-normal cursor-pointer"
                        >
                          Use {selectedCountry.name} template
                        </Label>
                      </div>
                    )}

                    {jurisdictions.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id="copy"
                            name="template"
                            value="copy"
                            checked={loadTemplate === "copy"}
                            onChange={(e) =>
                              setLoadTemplate(e.target.value as any)
                            }
                            className="h-4 w-4"
                          />
                          <Label
                            htmlFor="copy"
                            className="font-normal cursor-pointer"
                          >
                            Copy from existing jurisdiction
                          </Label>
                        </div>
                        {loadTemplate === "copy" && (
                          <Select
                            value={copyFromJurisdiction}
                            onValueChange={setCopyFromJurisdiction}
                          >
                            <SelectTrigger className="ml-6 w-[calc(100%-1.5rem)]">
                              <SelectValue placeholder="Select jurisdiction..." />
                            </SelectTrigger>
                            <SelectContent>
                              {jurisdictions.map((j) => (
                                <SelectItem key={j.id} value={j.id}>
                                  {getCountryByCode(j.countryCode)?.flag}{" "}
                                  {j.countryName}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAddJurisdictionOpen(false);
                resetAddForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddJurisdiction}
              disabled={
                !selectedCountry || !primaryCurrency || !consolidationCurrency
              }
            >
              Add Jurisdiction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Jurisdiction?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{" "}
              <strong>{selectedJurisdiction?.countryName}</strong>?
              <br />
              <br />
              This will delete all statutory payment schedules and configuration
              for this jurisdiction. Cash flow data will no longer be tracked
              for this country.
              <br />
              <br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedJurisdiction(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteJurisdiction}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove Jurisdiction
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
